import React, { useState, useCallback } from 'react';
import { ChevronDown, ChevronRight, AlertCircle, FileText } from 'lucide-react';
import adminAPI from '../../../api/admin';

/**
 * Payment Audit Panel — collapsible per-booking strip that shows every
 * Pocket Pay webhook callback we've received for this booking.
 *
 * Built for ThreeG Media / Pocket Pay dispute resolution: when the
 * gateway claims they sent (or didn't send) a callback, the admin can
 * expand this and see the exact raw payload we received with timestamp.
 *
 * Lazy-loaded: only fetches when the admin first expands the panel —
 * never on the booking-list page render, so no N+1.
 *
 * Only mounted for bookings with a `pocket_pay_order_id` (see
 * BookingCard.jsx mount guard).
 */
const fmtTs = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch {
    return iso;
  }
};

const STATUS_PILL = {
  SUCCESS:   'bg-emerald-100 text-emerald-800 border-emerald-200',
  COMPLETED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  FAILED:    'bg-rose-100 text-rose-800 border-rose-200',
  CANCELLED: 'bg-rose-100 text-rose-800 border-rose-200',
  PENDING:   'bg-amber-100 text-amber-800 border-amber-200',
};

function CallbackRow({ row }) {
  // Each row shows: timestamp · status pill · txn id · "view raw" toggle.
  // Raw payload is kept collapsed by default — most of the time the chips
  // tell admins everything they need. Only opened during forensics.
  const [showRaw, setShowRaw] = useState(false);
  const status = (row.status || '').toUpperCase();
  const pill = STATUS_PILL[status] || 'bg-slate-100 text-slate-700 border-slate-200';
  return (
    <li
      className="border border-slate-200 rounded-md bg-white px-3 py-2 text-xs"
      data-testid="payment-callback-row"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-slate-700">{fmtTs(row.received_at)}</span>
        <span
          className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${pill}`}
          data-testid={`callback-status-${status.toLowerCase()}`}
        >
          {status || 'UNKNOWN'}
        </span>
        <span className="font-mono text-slate-500 truncate max-w-[220px]" title={row.transaction_id || ''}>
          txn: {row.transaction_id || '—'}
        </span>
        <button
          type="button"
          onClick={() => setShowRaw((s) => !s)}
          className="ml-auto inline-flex items-center gap-1 text-[10px] font-medium text-slate-600 hover:text-slate-900"
          data-testid="callback-raw-toggle"
        >
          <FileText className="w-3 h-3" />
          {showRaw ? 'Hide raw' : 'View raw'}
        </button>
      </div>
      {showRaw && (
        <pre
          className="mt-2 bg-slate-50 border border-slate-200 rounded p-2 text-[10px] font-mono text-slate-700 overflow-x-auto"
          data-testid="callback-raw-payload"
        >
          {JSON.stringify(row.full_payload || row, null, 2)}
        </pre>
      )}
    </li>
  );
}

export default function PaymentAuditPanel({ bookingId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null); // { callbacks: [...] }

  const handleToggle = useCallback(async () => {
    const next = !open;
    setOpen(next);
    // Fetch only on the first expand. Subsequent expands reuse the
    // already-loaded ledger — admins are unlikely to need refresh
    // mid-session (webhook arrivals are rare).
    if (next && data === null && !loading) {
      setLoading(true);
      setError(null);
      try {
        const res = await adminAPI.getBookingPaymentCallbacks(bookingId);
        setData(res);
      } catch (e) {
        setError(e?.response?.data?.detail || e?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    }
  }, [open, data, loading, bookingId]);

  const callbacks = data?.callbacks || [];
  const count = callbacks.length;

  return (
    <div
      className="border-t pt-2 mt-1"
      data-testid="payment-audit-panel"
    >
      <button
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 hover:text-slate-900"
        aria-expanded={open}
        data-testid="payment-audit-toggle"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        Payment Audit
        {data !== null && (
          <span className="ml-1 inline-flex min-w-[1.25rem] justify-center text-[10px] font-bold bg-slate-200 text-slate-700 rounded-full px-1.5">
            {count}
          </span>
        )}
      </button>

      {open && (
        <div className="mt-2" data-testid="payment-audit-body">
          {loading && (
            <div className="text-xs text-slate-500" data-testid="payment-audit-loading">
              Loading callbacks…
            </div>
          )}
          {error && !loading && (
            <div
              className="text-xs text-rose-700 inline-flex items-center gap-1"
              data-testid="payment-audit-error"
            >
              <AlertCircle className="w-3 h-3" />
              {error}
            </div>
          )}
          {!loading && !error && data && count === 0 && (
            <div className="text-xs text-slate-500 italic" data-testid="payment-audit-empty">
              No callbacks received yet for this booking.
            </div>
          )}
          {!loading && !error && count > 0 && (
            <ul className="space-y-1.5" data-testid="payment-audit-list">
              {callbacks.map((row, idx) => (
                <CallbackRow key={row.id || `${row.transaction_id}-${idx}`} row={row} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
